import { api, type AdminWorkflowJob, type DashboardPageData } from './api-client';
import { fail, fromApiResult, okVoid, type DataResult, type DataVoidResult } from './data-result';
import { escapeHtml } from './html';
import { EMAIL_VERIFICATION_SETTINGS_HREF } from './dashboard-work-queue';
import { buildServiceRecovery, type ServiceRecoveryAction } from './service-recovery-display';
import type { CreateAccessTokenRequest, CreateAccessTokenResponse } from '../types/api';

export interface DashboardActionMessage {
  title: string;
  body: string;
  actionHref?: string;
  actionLabel?: string;
  icon: string;
  tone: 'amber' | 'error' | 'success';
  recovery?: ServiceRecoveryAction;
}

export async function loadDashboardData(): Promise<DataResult<DashboardPageData>> {
  try {
    return fromApiResult(await api.dashboard.get(), 'Failed to load dashboard data.');
  } catch (_error) {
    return fail('Network error - could not load dashboard. Please check your connection and refresh.');
  }
}

export async function createAccessToken(request: CreateAccessTokenRequest): Promise<DataResult<CreateAccessTokenResponse>> {
  return fromApiResult(await api.accessTokens.create(request), 'Failed to create access token');
}

export async function revokeAccessToken(id: number): Promise<DataVoidResult> {
  const result = await api.accessTokens.revoke(id);
  return result.success ? okVoid() : fail(result.error || 'Failed to revoke token');
}

export function buildAccessTokenCreationErrorMessage(error?: string | null): DashboardActionMessage {
  const message = String(error || '').trim();
  const lower = message.toLowerCase();
  if (lower.includes('verify your email') || lower.includes('email verification')) {
    const recovery = buildServiceRecovery('email_verification_required', message, {
      actionHref: EMAIL_VERIFICATION_SETTINGS_HREF,
      actionLabel: 'Verify email',
    });
    return {
      title: 'Verify your email first',
      body: 'SkillFab requires a verified account email before creating agent access tokens.',
      actionHref: EMAIL_VERIFICATION_SETTINGS_HREF,
      actionLabel: 'Verify email',
      icon: 'fa-envelope-circle-check',
      tone: 'amber',
      recovery,
    };
  }
  return {
    title: 'Could not create token',
    body: message || 'Check the token name and try again.',
    icon: 'fa-circle-exclamation',
    tone: 'error',
    recovery: buildServiceRecovery('generic', message),
  };
}

export function buildAccessTokenRevokeErrorMessage(error?: string | null): DashboardActionMessage {
  const body = String(error || '').trim() || 'Check whether the token still exists, then try again.';
  return {
    title: 'Could not revoke token',
    body,
    icon: 'fa-circle-exclamation',
    tone: 'error',
    recovery: buildServiceRecovery('generic', body),
  };
}

export function buildAccessTokenRevokeSuccessMessage(): DashboardActionMessage {
  return {
    title: 'Token revoked',
    body: 'Connected clients using that token can no longer access SkillFab.',
    icon: 'fa-check-circle',
    tone: 'success',
  };
}

let allJobs: AdminWorkflowJob[] = [];
let currentFilter = '';

function renderAdminStats(stats: { users: number; skills: number; total_downloads: number }): void {
  const container = document.getElementById('admin-stats');
  if (!container) return;
  container.innerHTML = [
    { label: 'Users', value: stats.users, icon: 'fa-users', color: 'text-blue-400' },
    { label: 'Skills', value: stats.skills, icon: 'fa-puzzle-piece', color: 'text-brand-400' },
    { label: 'Downloads', value: stats.total_downloads, icon: 'fa-download', color: 'text-amber-400' },
  ].map((item) => '<div class="stat-glow rounded-xl p-4"><div class="flex items-center gap-2 mb-2"><i class="fas ' + item.icon + ' ' + item.color + '"></i><span class="text-xs text-zinc-500">' + item.label + '</span></div><div class="text-xl font-bold text-white">' + item.value + '</div></div>').join('');
}

function renderAdminLogs(logs: Array<{ issue_id?: number | null; issue_title?: string | null; action: string; message?: string | null; created_at?: string | null }>): void {
  const container = document.getElementById('admin-logs');
  if (!container) return;
  container.innerHTML = logs.map((log) => {
    const issue = log.issue_title || (log.issue_id ? `Issue #${log.issue_id}` : 'System');
    const createdAt = log.created_at ? window.timeAgo(log.created_at) : 'unknown';
    return '<div class="flex items-center gap-3 p-3 bg-surface-1 rounded-lg text-sm">' +
      '<span class="text-xs text-zinc-600 w-24 flex-shrink-0">' + createdAt + '</span>' +
      '<span class="text-zinc-400">[' + escapeHtml(issue) + ']</span>' +
      '<span class="text-zinc-300">' + escapeHtml(log.action) + '</span>' +
      '<span class="text-zinc-500 truncate flex-1">' + escapeHtml(log.message || '') + '</span>' +
    '</div>';
  }).join('') || '<p class="text-sm text-zinc-600 py-4">No recent activity</p>';
}

function renderJobs(jobs: AdminWorkflowJob[]): void {
  allJobs = jobs;
  filterJobs(currentFilter);
}

export function filterJobs(filter: string): void {
  currentFilter = filter;
  document.querySelectorAll('.jobs-filter-btn').forEach((button) => {
    button.classList.toggle('active', button.textContent!.toLowerCase() === (filter || 'all'));
  });
  const normalized = filter === 'succeeded' ? 'completed' : filter;
  const jobs = normalized
    ? allJobs.filter((job) => job.status === normalized || job.kind.includes(normalized))
    : allJobs;
  const container = document.getElementById('admin-jobs');
  if (!container) return;
  if (!jobs.length) {
    container.innerHTML = '<p class="text-sm text-zinc-600 py-4">No workflow jobs recorded yet</p>';
    return;
  }
  container.innerHTML = jobs.map((job) => {
    const failed = job.status === 'failed' || job.status === 'degraded';
    const badge = failed ? 'text-red-400 border-red-800 bg-red-950/30'
      : job.status === 'completed' ? 'text-green-400 border-green-800 bg-green-950/30'
      : 'text-yellow-400 border-yellow-800 bg-yellow-950/30';
    const entities = [
      job.skillName ? 'skill:' + job.skillName : null,
      job.submissionId ? 'submission #' + job.submissionId : null,
      job.issueId ? 'issue #' + job.issueId : null,
    ].filter(Boolean).map((label) =>
      '<span class="text-xs px-1.5 py-0.5 rounded border border-zinc-700 bg-zinc-900 text-zinc-400">' + escapeHtml(String(label)) + '</span>'
    ).join('');
    const error = failed && job.lastError
      ? '<div class="mt-1 text-xs text-red-400/80 pl-2 border-l border-red-800">' + escapeHtml(job.lastError) + '</div>'
      : '';
    return '<div class="p-3 bg-surface-1 rounded-lg text-sm"><div class="flex items-center gap-2 flex-wrap">' +
      '<span class="text-xs text-zinc-600 font-mono">' + escapeHtml(job.idempotencyKey.slice(0, 18)) + '</span>' + entities +
      '<span class="text-xs px-1.5 py-0.5 rounded border ' + badge + '">' + escapeHtml(job.status) + '</span>' +
      '<span class="text-xs text-zinc-400">' + escapeHtml(job.kind) + '</span>' +
      '<span class="text-xs text-zinc-600 ml-auto">' + window.timeAgo(job.updatedAt || job.createdAt) + '</span>' +
    '</div>' + error + '</div>';
  }).join('');
}

export async function loadDashboardOperatorData(): Promise<void> {
  try {
    const [stats, logs, jobs] = await Promise.all([
      api.admin.getStats(),
      api.admin.getLogs({ limit: 20 }),
      api.admin.listWorkflowJobs({ limit: 50 }),
    ]);
    if (stats.success) renderAdminStats(stats.data);
    if (logs.success) renderAdminLogs(logs.data || []);
    if (jobs.success) renderJobs(jobs.data.jobs || []);
  } catch (error) {
    console.error('Failed to load admin data:', error);
  }
}
